import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { OrganisationClaim, PasswordSignInResult } from "shell/api/types";

import { useForgotPasswordFlow } from "common-services/hooks/useForgotPasswordFlow";
import { translateError } from "common-services/utils/passwordErrors";
import { saveUserOrgPreference } from "generated/api/organisations/user-preferences/user-preferences";
import {
  completeNewPasswordChallenge,
  confirmPasswordReset,
  refreshCognitoTokens,
  requestPasswordReset,
  signInWithPassword,
  getOrganisationsFromAccessToken,
  getUser,
} from "shell/api/auth";
import { useAsyncSubmit } from "shell/api/hooks";

type PasswordLoginView =
  | { kind: "credentials" }
  | { kind: "newPassword"; username: string; session: string }
  | { kind: "forgotPassword" }
  | { kind: "forgotConfirm"; username: string }
  | {
      kind: "orgSelection";
      organisations: OrganisationClaim[];
      refreshToken: string;
    };

interface UsePasswordLoginFlowArgs {
  initialEmail?: string;
  onAuthenticated: () => void;
}

export const usePasswordLoginFlow = ({
  initialEmail,
  onAuthenticated,
}: UsePasswordLoginFlowArgs) => {
  const { t } = useTranslation(["common"]);
  const [view, setView] = useState<PasswordLoginView>({ kind: "credentials" });
  const [emailHint, setEmailHint] = useState(initialEmail || "");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const navigateTo = (
    view: PasswordLoginView,
    options?: {
      info?: string;
    },
  ) => {
    setError("");
    setInfo(options?.info ?? "");
    setView(view);
  };

  const { submitting, wrapSubmit } = useAsyncSubmit(setError);

  const handleSignInResult = (result: PasswordSignInResult) => {
    switch (result.kind) {
      case "ok": {
        const user = getUser();
        const organisations = user
          ? getOrganisationsFromAccessToken(user.access_token)
          : [];

        if (organisations.length > 1 && user?.refresh_token) {
          setError("");
          setView({
            kind: "orgSelection",
            organisations,
            refreshToken: user.refresh_token,
          });
          break;
        }

        onAuthenticated();
        break;
      }

      case "newPasswordRequired": {
        setError("");
        setView({
          kind: "newPassword",
          username: result.username,
          session: result.session,
        });

        break;
      }

      case "mfaRequired": {
        setError(t("common:passwordLogin.mfaUnsupported"));
        break;
      }

      default: {
        setError(translateError(result.code, result.message, t));
      }
    }
  };

  const submitCredentials = wrapSubmit(
    async ({ email, password }: { email: string; password: string }) => {
      setInfo("");
      setEmailHint(email);
      handleSignInResult(await signInWithPassword(email, password));
    },
  );

  const submitNewPassword = wrapSubmit(
    async ({ newPassword }: { newPassword: string }) => {
      if (view.kind !== "newPassword") return;
      handleSignInResult(
        await completeNewPasswordChallenge(
          view.username,
          newPassword,
          view.session,
        ),
      );
    },
  );

  const { submitForgotPassword, submitForgotConfirm } = useForgotPasswordFlow({
    requestPasswordReset,
    confirmPasswordReset,
    forgotConfirmUsername:
      view.kind === "forgotConfirm" ? view.username : undefined,
    wrapSubmit,
    setEmailHint,
    setError,
    onRequested: email => setView({ kind: "forgotConfirm", username: email }),
    onConfirmed: () =>
      navigateTo(
        { kind: "credentials" },
        { info: t("common:passwordLogin.forgotPassword.successInfo") },
      ),
  });

  const submitOrgSelection = wrapSubmit(
    async ({ organisationId }: { organisationId: string }) => {
      if (view.kind !== "orgSelection") return;

      try {
        await saveUserOrgPreference({ organisation_id: organisationId });
        const refreshed = await refreshCognitoTokens(view.refreshToken);

        if (!refreshed) {
          setError(t("common:passwordLogin.orgSelection.error"));
          return;
        }

        onAuthenticated();
      } catch {
        setError(t("common:passwordLogin.orgSelection.error"));
      }
    },
  );

  return {
    view,
    emailHint,
    submitting,
    error,
    info,
    submitCredentials,
    submitNewPassword,
    submitForgotPassword,
    submitForgotConfirm,
    submitOrgSelection,
    navigateTo,
  };
};
